import React, { FC, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { Alert, Box, CircularProgress, Modal, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { State } from "../../redux/slices";
import { fetchSharedResult } from "../../redux/actions/data/sharedResultAsyncActions";
import {
  setSampleViewInfo,
  setSampleViewPhotos,
  setOpenCloseModalPhotos,
} from "../../redux/slices/data/dataSampleView";
import DataSampleViewLeft from "../data/dataSampleView/DataSampleLeftSide";
import DataSampleViewRight from "../data/dataSampleView/DataSampleRightSide";
import { BodyCarouselPhotos } from "../data/dataSampleView/DataSampleViewCarouselModal";
import logoInarix from "../../assets/images/inarix-logo-h@3x.png";
import { styles, logoStyle } from "./SharedResultPage.styles";
import moment from "moment";
import { findTheName } from "../data/dataSampleView/DataSampleLeftSide";
import ComputedStatusChip from "../dataTable/table/columnsAndActions/ComputedStatusChip";
import Annotation from "../data/dataSampleView/DataSampleViewAnnotation";

const SharedResultPage: FC = () => {
  const [t] = useTranslation("global");
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();

  const { loading, error, errorStatus, data } = useSelector(
    (state: State) => state.SharedResultReducer.sharedData,
  );
  const { sampleViewInfo, modalPhotos } = useSelector(
    (state: State) => state.DataSampleViewReducer,
  );
  const bodyCarouselPhotosValues = BodyCarouselPhotos();

  useEffect(() => {
    if (id) {
      dispatch(fetchSharedResult({ id }));
    }
  }, [id]);

  useEffect(() => {
    if (data) {
      dispatch(setSampleViewInfo(data));

      const signedPhotos = (data.sample?.acquisitions ?? [])
        .flatMap((acq: any) => acq.images ?? [])
        .filter((img: any) => img.signedRequest)
        .map((img: any) => ({ signedRequest: img.signedRequest }));

      dispatch(setSampleViewPhotos(signedPhotos));
    }
  }, [data]);

  const texts = sampleViewInfo?.texts as { [key: string]: string } | undefined;
  const cropTextId = sampleViewInfo?.cropTextId;
  const scenarioNameTextId = sampleViewInfo?.scenarioNameTextId;
  const createdAt = moment(sampleViewInfo?.createdAt);
  const createdAtModify = createdAt.isValid() ? createdAt.format("l") : "";

  const renderError = () => {
    const is410 = errorStatus === 410;
    return (
      <Box sx={styles.errorBox}>
        <Alert severity={is410 ? "warning" : "error"} sx={styles.errorAlert}>
          <Typography fontWeight={600} gutterBottom>
            {is410
              ? t("Data.SampleView.share.link_expired_title")
              : t("Data.SampleView.share.link_not_found_title")}
          </Typography>
          {is410
            ? t("Data.SampleView.share.link_expired_body")
            : t("Data.SampleView.share.link_not_found_body")}
        </Alert>
      </Box>
    );
  };

  return (
    <Box sx={styles.root}>
      <Box sx={styles.header}>
        <img src={logoInarix} alt="Inarix" style={logoStyle} />
      </Box>

      <Box sx={styles.content}>
        {loading && (
          <Box sx={styles.loadingBox}>
            <CircularProgress />
          </Box>
        )}

        {!loading && (error || errorStatus) && renderError()}

        {!loading && data && sampleViewInfo && (
          <>
            <div className="sample-header">
              <div className="sample-info-container">
                <div>
                  <div className="sample-id-title">
                    {`${findTheName(scenarioNameTextId, texts)}, ${findTheName(cropTextId, texts)} - ${createdAtModify}`}
                  </div>
                  <div className="sample-id-remote">
                    {`${t("Data.Columns.InarixID")} : ${sampleViewInfo?.pastActionId}`}
                  </div>
                </div>
              </div>
              <ComputedStatusChip statusId={sampleViewInfo?.computedStatusId} />
            </div>

            <div className="left-right-contain">
              <DataSampleViewLeft />
              <DataSampleViewRight />
            </div>
            <Annotation />
          </>
        )}
      </Box>

      <Modal
        open={modalPhotos}
        onClose={() => dispatch(setOpenCloseModalPhotos(false))}
      >
        {bodyCarouselPhotosValues}
      </Modal>
    </Box>
  );
};

export default SharedResultPage;
